import { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/AppShell";
import { PageHeader, Panel, StatusPill, EmptyState } from "../../components/Bits";
import { Modal } from "./Students";
import { api, formatApiError } from "../../lib/api";
import { Plus, Trash2, Check, Ban } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { v: "damage", l: "Damage" },
  { v: "late_payment", l: "Late payment" },
  { v: "rule_violation", l: "Rule violation" },
  { v: "lost_property", l: "Lost property" },
  { v: "other", l: "Other" },
];

const CAT_TINT = {
  damage: "bg-[#F5D8D8] text-[#8C3F3F]",
  late_payment: "bg-[#FBEED7] text-[#8C6D31]",
  rule_violation: "bg-[#E8C2B8] text-[#8C3F2C]",
  lost_property: "bg-[#D6E6EB] text-[#3D5C68]",
  other: "bg-[#F4F1EA] text-[#75716E]",
};

export default function AdminFines() {
  const [rows, setRows] = useState(null);
  const [students, setStudents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", category: "damage", title: "", description: "", amount: "", incident_date: "" });
  const [saving, setSaving] = useState(false);

  async function load() {
    const [f, s] = await Promise.all([api.get("/admin/fines"), api.get("/admin/students")]);
    setRows(f.data); setStudents(s.data);
  }
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    const r = rows || [];
    return {
      pending: r.filter((x) => x.status === "pending").reduce((a, b) => a + Number(b.amount), 0),
      paid: r.filter((x) => x.status === "paid").reduce((a, b) => a + Number(b.amount), 0),
      waived: r.filter((x) => x.status === "waived").reduce((a, b) => a + Number(b.amount), 0),
    };
  }, [rows]);

  const filtered = (rows || []).filter((f) => filter === "all" ? true : f.status === filter);

  async function save(e) {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/admin/fines", { ...form, amount: Number(form.amount) });
      toast.success("Fine recorded");
      setOpen(false);
      setForm({ student_id: "", category: "damage", title: "", description: "", amount: "", incident_date: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function setStatus(f, status) {
    try { await api.put(`/admin/fines/${f.id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function remove(f) {
    if (!window.confirm("Delete this fine?")) return;
    await api.delete(`/admin/fines/${f.id}`);
    toast.success("Deleted"); load();
  }

  return (
    <AppShell>
      <PageHeader
        overline="Discipline"
        title="Fines & damages"
        subtitle="Record damages or rule-violation charges and track payment."
        actions={<button onClick={() => setOpen(true)} data-testid="add-fine-btn" className="bg-terracotta text-white px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Record fine</button>}
      />

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <Stat label="Pending" value={`₹ ${totals.pending.toLocaleString()}`} tint="bg-[#FBEED7]" />
        <Stat label="Paid" value={`₹ ${totals.paid.toLocaleString()}`} tint="bg-[#DCE3D6]" />
        <Stat label="Waived" value={`₹ ${totals.waived.toLocaleString()}`} tint="bg-[#EAE5DE]" />
      </div>

      <Panel
        title={`${filtered.length} ${filtered.length === 1 ? "fine" : "fines"}`}
        action={
          <div className="flex gap-1 bg-[#F4F1EA] rounded-md p-1 text-sm">
            {["all", "pending", "paid", "waived"].map((k) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`fine-filter-${k}`} className={`px-3 py-1 rounded-md capitalize ${filter === k ? "bg-white text-deep shadow-sm" : "text-warm"}`}>{k}</button>
            ))}
          </div>
        }
      >
        {rows === null ? <div className="text-sm text-warm">Loading…</div>
          : filtered.length === 0 ? <EmptyState title="No fines" hint="Quiet times! No damages or fines recorded." />
            : (
              <div className="overflow-x-auto -mx-6">
                <table className="min-w-full text-sm">
                  <thead><tr className="bg-[#F4F1EA] text-warm uppercase tracking-wider text-xs">
                    <th className="text-left px-6 py-3">Resident</th>
                    <th className="text-left px-6 py-3">Category</th>
                    <th className="text-left px-6 py-3">Description</th>
                    <th className="text-left px-6 py-3">Date</th>
                    <th className="text-left px-6 py-3">Amount</th>
                    <th className="text-left px-6 py-3">Status</th>
                    <th className="text-right px-6 py-3">Actions</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map((f) => (
                      <tr key={f.id} className="border-t border-warm hover:bg-[#FDFBF7]" data-testid={`fine-row-${f.id}`}>
                        <td className="px-6 py-3"><div className="font-medium text-deep">{f.student_name}</div><div className="text-xs text-warm">Room {f.room_number || "—"}</div></td>
                        <td className="px-6 py-3"><span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${CAT_TINT[f.category] || CAT_TINT.other}`}>{f.category.replace("_", " ")}</span></td>
                        <td className="px-6 py-3 max-w-xs"><div className="font-medium text-deep">{f.title}</div>{f.description && <div className="text-xs text-warm line-clamp-1">{f.description}</div>}</td>
                        <td className="px-6 py-3">{f.incident_date}</td>
                        <td className="px-6 py-3 font-medium">₹ {Number(f.amount).toLocaleString()}</td>
                        <td className="px-6 py-3"><StatusPill status={f.status} /></td>
                        <td className="px-6 py-3 text-right whitespace-nowrap">
                          {f.status === "pending" && (
                            <>
                              <button onClick={() => setStatus(f, "paid")} data-testid={`fine-paid-${f.id}`} title="Mark paid" className="p-2 hover:bg-[#DCE3D6]/60 rounded-md"><Check className="h-4 w-4 text-[#4A5D3E]" /></button>
                              <button onClick={() => setStatus(f, "waived")} data-testid={`fine-waive-${f.id}`} title="Waive" className="p-2 hover:bg-[#EAE5DE] rounded-md"><Ban className="h-4 w-4 text-[#5A5652]" /></button>
                            </>
                          )}
                          <button onClick={() => remove(f)} className="p-2 hover:bg-[#F5D8D8] rounded-md"><Trash2 className="h-4 w-4 text-[#8C3F3F]" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </Panel>

      {open && (
        <Modal title="Record a fine" onClose={() => setOpen(false)}>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
            <label className="block sm:col-span-2"><span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">Resident *</span>
              <select required value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })} className={input} data-testid="fine-student-select">
                <option value="">Select…</option>
                {students.map((s) => <option key={s.id} value={s.id}>{s.name} — Room {s.room_number || "—"}</option>)}
              </select>
            </label>
            <FieldL label="Category *">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={input}>
                {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </FieldL>
            <FieldL label="Incident date"><input type="date" value={form.incident_date} onChange={(e) => setForm({ ...form, incident_date: e.target.value })} className={input} /></FieldL>
            <div className="sm:col-span-2"><FieldL label="Short title *"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={input} placeholder="e.g. Broken chair in room 102" data-testid="fine-title" /></FieldL></div>
            <div className="sm:col-span-2"><FieldL label="Description"><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={input} /></FieldL></div>
            <FieldL label="Amount (₹) *"><input required type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={input} data-testid="fine-amount" /></FieldL>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-5 py-2 rounded-md text-sm border border-warm">Cancel</button>
              <button disabled={saving} type="submit" data-testid="save-fine-btn" className="bg-terracotta text-white px-6 py-2 rounded-md text-sm disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
            </div>
          </form>
        </Modal>
      )}
    </AppShell>
  );
}

function Stat({ label, value, tint }) {
  return (
    <div className="bg-white border border-warm rounded-lg p-5">
      <div className={`inline-block h-1.5 w-12 rounded-full mb-3 ${tint}`} />
      <div className="font-body font-semibold text-3xl text-deep tracking-tight">{value}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-warm mt-1">{label}</div>
    </div>
  );
}

const input = "w-full px-3 py-2 rounded-md border border-warm bg-white focus:outline-none focus:ring-2 focus:ring-[#E8C2B8] focus:border-[#C86F58] text-sm";
function FieldL({ label, children }) { return <label className="block"><span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">{label}</span>{children}</label>; }
